import axios, { AxiosRequestConfig, AxiosResponse } from 'axios';
import i18n from 'i18n-js';

import userManager from 'managers/userManager';
import type { ApplicationState } from 'store';

import {
    Restaurants,
    Users,
    Roles,
    Menus,
    Common,
    Category,
    Order,
    User,
    Products,
    Payments,
    Catalog,
    AppUpdates,
} from './controllers';
import { LocalStorageKeys } from 'constants/localStorageKeys';
import { PrinterServers } from './controllers/printer-server';

// Function to get store dynamically (avoids circular dependencies)
function getStore() {
    // Import store dynamically to avoid circular dependencies
    const { store } = require('../index');
    return store;
}

// Function to get current Redux state
export function getReduxState(): ApplicationState {
    return getStore().getState();
}

axios.defaults.baseURL = process.env.REACT_APP_API;
export const identityUrl = process.env.REACT_APP_IDENTITY;
userManager.getUser().then((user) => {
    if (user && !user.expired) {
        axios.defaults.headers.common['Authorization'] = 'Bearer ' + user.access_token;
        //axios.defaults.headers.common['timezone'] = getBrowserTimezone();
    }
});

// Request interceptor to add Redux state data to requests if needed
axios.interceptors.request.use(
    (config) => {
        try {
            // Skip adding restaurant header for specific endpoints
            const url = config.url || '';
            if (!url.includes('/currentrestaurant/')) {
                const state = getReduxState();
                if (state.user?.currentRestaurantId) {
                    config.headers['X-Current-Restaurant-Id'] = state.user.currentRestaurantId;
                }
            }
        } catch (error) {
            console.warn('Could not access Redux store in request interceptor:', error);
        }

        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

class ApiError {
    constructor(
        readonly StatusCode: number,
        readonly Message: string,
        readonly Code?: string,
        readonly TableId?: string,
        readonly Tables?: Array<{ tableId: string; tableName: string }>
    ) {}

    get message(): string {
        return this.Message;
    }

    toString(): string {
        return this.Message;
    }
}

axios.interceptors.response.use(
    (response) => {
        return response;
    },
    (error) => {
        if (!error.response) {
            return Promise.reject(i18n.t('warnings.connectionError'));
        } else if (400 === error.response.status) {
            if (error.config?.url?.includes('Restaurant/reservation-module') &&
                error.response.data?.Code === 'reservationBlockedDatesInvalid') {
                return Promise.reject(error.response.data);
            }
            return Promise.reject(
                error.response?.data?.Message || i18n.t('warnings.wrongDataProvided')
            );
        } else if (401 === error.response.status) {
            userManager.signoutRedirect();
            userManager.removeUser();
            return Promise.reject(i18n.t('warnings.sessionExpired'));
        } else if (404 === error.response.status) {
            return Promise.reject(i18n.t('warnings.pageNotFound'));
        } else if (409 === error.response.status) {
            return Promise.reject(
                new ApiError(
                    error.response.status,
                    error.response.data.Message || i18n.t('common.somethingWrong'),
                    error.response.data.Code,
                    error.response.data.TableId,
                    error.response.data.Tables
                )
            );
        } else if (500 === error.response.status) {
            return Promise.reject(error.response.data.Message || i18n.t('common.somethingWrong'));
        } else {
            return Promise.reject(i18n.t('common.somethingWrong'));
        }
    }
);

export function prepareHeaders(noJson: boolean = false) {
    const language = localStorage.getItem(LocalStorageKeys.jamesLanguage) || 'de';
    return {
        headers: noJson
            ? {
                  'Accept-Language': language,
              }
            : {
                  'Content-Type': 'application/json;charset=UTF-8',
                  'Accept-Language': language,
              },
    };
}

const responseBody = (response: AxiosResponse) => response.data;

export const requests = {
    get: (url: string, params?: {}) =>
        axios
            .get(url, {
                ...params,
                ...prepareHeaders(true),
            })
            .then(responseBody),
    post: (url: string, body: {} = {}) =>
        axios.post(url, body, prepareHeaders()).then(responseBody),
    patch: (url: string, body: {}) => axios.patch(url, body, prepareHeaders()).then(responseBody),
    put: (url: string, body: {}, config: AxiosRequestConfig | undefined) =>
        axios.put(url, body, config).then(responseBody),
    delete: (url: string, data?: {}) =>
        axios
            .delete(url, {
                data,
                ...prepareHeaders(true),
            })
            .then(responseBody),
};

/* const IdentityServer = {
    SignIn: () => requests.get(''),
    SignUp: (email: string, password: string) => requests.post('', { email, password }),
}; */

const agent = {
    requests,
    /* IdentityServer, */
    Restaurants,
    Users,
    Roles,
    Menus,
    Common,
    Category,
    Order,
    User,
    Products,
    Payments,
    Catalog,
    PrinterServers,
    AppUpdates,
};

export default agent;
